import { inject, Injectable } from '@angular/core';

import { WINDOW } from '@rpgc-ai/shared-interfaces/tokens/window.token';

import type { RuntimeTransport, StreamConnection, StreamRequestBody } from '@rpgcai/game-runtime';

const JSON_HEADERS: Record<string, string> = { 'Content-Type': 'application/json' };

/**
 * Frontend-only marker that arms game-runtime build.727's extension-gated
 * AutosaveManager. It is deliberately not a UUID, and every transport boundary
 * below rejects or strips it before a request can leave the browser.
 */
export const WORKFLOW_ONLY_AUTOSAVE_EXTENSION_SENTINEL = '__workflow_only_autosave__';

/**
 * Per-call save-state options owned by rpgc_backend. rpgc_backend's
 * `/runtime/play/save-state` REQUIRES `chatId`+`workflowId` to mirror state into
 * `ChatWorkflow.storageData`, so this contract lives here rather than depending
 * on the package's `SaveStateOptions` export (which targets its reference
 * backend's narrower `SaveStateDto`).
 */
export interface SaveStateOptions {
  chatId?: string;
  workflowId?: string;
}

@Injectable({ providedIn: 'root' })
export class WorkflowTransportService implements RuntimeTransport {
  readonly #window = inject(WINDOW);
  #baseUrl = '';
  #token = '';
  #workflowId = '';
  #extensionId = '';

  /** Public for sendBeacon URL composition in chat.component (D3 cleanup path). */
  get baseUrl(): string {
    return this.#baseUrl;
  }

  configure(baseUrl: string, token?: string): void {
    this.#baseUrl = baseUrl;
    this.#token = token ?? '';
  }

  setWorkflowId(id: string): void {
    this.#workflowId = id;
  }

  setExtensionId(id: string): void {
    this.#extensionId = id;
  }

  /**
   * Persist game state. Per-call opts (D1) — singleton state intentionally NOT read here.
   * Backend mirrors to ChatWorkflow.storageData when both chatId+workflowId present
   * AND key 'Default Storage' exists in storages.
   */
  async saveState(extensionId: string, storages: Record<string, Record<string, unknown>>, opts?: SaveStateOptions): Promise<void> {
    if (extensionId === WORKFLOW_ONLY_AUTOSAVE_EXTENSION_SENTINEL) {
      const workflowId = opts?.workflowId?.trim();
      if (!workflowId) {
        throw new Error('Workflow-only autosave requires an explicit workflowId');
      }
      await this.#post('/runtime/play/save-state', { workflowId, storages });
      return;
    }

    const body: Record<string, unknown> = { extensionId, storages };
    if (opts?.chatId) {
      body['chatId'] = opts.chatId;
    }
    if (opts?.workflowId) {
      body['workflowId'] = opts.workflowId;
    }
    await this.#post('/runtime/play/save-state', body);
  }

  /**
   * Synchronous best-effort persistence for page-unload / teardown
   * (`beforeunload` / `pagehide` / deliberate destroy), where an async `fetch`
   * would be cancelled. Mirrors `saveState` body shape; omits undefined opts
   * keys to stay byte-compatible. Cookie-auth via the browser (sendBeacon sends
   * credentials; no Authorization header — same as the prior chat beacon).
   * Returns whether the beacon was queued (`false` when `sendBeacon` is
   * unavailable — SSR / non-browser).
   */
  saveStateBeacon(extensionId: string, storages: Record<string, Record<string, unknown>>, opts?: SaveStateOptions): boolean {
    let body: Record<string, unknown>;
    if (extensionId === WORKFLOW_ONLY_AUTOSAVE_EXTENSION_SENTINEL) {
      const workflowId = opts?.workflowId?.trim();
      if (!workflowId) {
        throw new Error('Workflow-only autosave requires an explicit workflowId');
      }
      body = { workflowId, storages };
    } else {
      body = { extensionId, storages };
      if (opts?.chatId) {
        body['chatId'] = opts.chatId;
      }
      if (opts?.workflowId) {
        body['workflowId'] = opts.workflowId;
      }
    }

    const nav = this.#window?.navigator;
    if (!nav || typeof nav.sendBeacon !== 'function') {
      return false;
    }
    const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
    return nav.sendBeacon(`${this.#baseUrl}/runtime/play/save-state`, blob);
  }

  async openStream(body: StreamRequestBody, signal?: AbortSignal): Promise<StreamConnection> {
    const controller = new AbortController();
    // Propagate the runtime's reconnect/teardown signal into our fetch controller
    // so an in-flight open can be aborted by the package (game-runtime ≥0.4 passes it).
    if (signal) {
      if (signal.aborted) {
        controller.abort();
      } else {
        signal.addEventListener('abort', () => controller.abort(), { once: true });
      }
    }
    // workflowId/extensionId are attached only when a consumer explicitly sets them
    // (Workshop module). Other modules leave them empty so body's own values flow.
    const workflowOnlyAutosave = body.extensionId === WORKFLOW_ONLY_AUTOSAVE_EXTENSION_SENTINEL;
    const payload: StreamRequestBody = workflowOnlyAutosave
      ? { ...body }
      : {
          ...body,
          ...(this.#workflowId ? { workflowId: this.#workflowId } : {}),
          ...(this.#extensionId ? { extensionId: this.#extensionId } : {}),
        };
    if (workflowOnlyAutosave) {
      // Do not let mutable singleton context redirect an attested Workshop run.
      // GameRuntime must pass its workflow id explicitly with the sentinel.
      const workflowId = body.workflowId?.trim();
      if (!workflowId) {
        throw new Error('Workflow-only runtime requires an explicit workflowId');
      }
      delete payload.extensionId;
      payload.workflowId = workflowId;
    }
    const res = await this.#post('/runtime/play/stream', payload, controller.signal);

    if (!res.body) {
      throw new Error('Response has no body');
    }

    return {
      reader: res.body.getReader(),
      abort: () => controller.abort(),
    };
  }

  async sendUIEvent(runId: string, elementId: string, event: string, value?: unknown): Promise<void> {
    await this.#post(`/runtime/play/ui-event/${runId}`, { elementId, event, value });
  }

  async respond(runId: string, action: string, value?: string): Promise<void> {
    await this.#post(`/runtime/play/respond/${runId}`, { action, value });
  }

  async get<T>(path: string): Promise<T> {
    const res = await this.#request(path, 'GET');
    return res.json() as Promise<T>;
  }

  async delete(path: string): Promise<void> {
    await this.#request(path, 'DELETE');
  }

  async #post(path: string, body: unknown, signal?: AbortSignal): Promise<Response> {
    return this.#request(path, 'POST', body, signal);
  }

  async #request(path: string, method: string, body?: unknown, signal?: AbortSignal): Promise<Response> {
    const headers: Record<string, string> = { ...JSON_HEADERS };

    if (this.#token) {
      headers['Authorization'] = `Bearer ${this.#token}`;
    }

    const res = await fetch(`${this.#baseUrl}${path}`, {
      method,
      headers,
      credentials: 'include',
      signal,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    if (!res.ok) {
      throw new Error(`${path} failed: HTTP ${res.status}`);
    }

    return res;
  }
}
